import React, { useState } from "react";
import * as Yup from "yup";
import {
  Box,
  Button,
  TextField,
  FormControl,
  Checkbox,
  Chip,
  Typography,
  Autocomplete,
} from "@mui/material";
import { useFormik } from "formik";
import {
  validationSchema,
  QuoteFormValues,
  initialValues,
} from "../validationSchema";
import NewPhoneNumberComponent from "../../../common/components/new-phone-input-component/new-phone-input-component";
import "./QuoteForm.scss";
import { enforceEmailPartLimits } from "../../../common/components/quote-modal-component/validationSchema";
interface QuoteFormContentProps {
  onSuccess?: () => void;
  partnerId: number;
  servicesOptions: any[];
  plannerServiceCategories?: string[];
  requestFn: (partnerId: number, data: any) => Promise<any>;
}

const QuoteFormContent: React.FC<QuoteFormContentProps> = ({
  onSuccess,
  partnerId,
  servicesOptions = [],
  plannerServiceCategories = [],
  requestFn,
}) => {
  const [countryCode, setCountryCode] = useState<string>("+1");

  const uniquePlannerCategories = Array.from(new Set(plannerServiceCategories || []));
  const hasPlannerCategories = uniquePlannerCategories.length > 0;

  const handleSubmit = async (
    values: QuoteFormValues,
    { resetForm }: { resetForm: () => void }
  ) => {
    try {
      // prepending country code to phone
      const finalValues = {
        ...values,
        phoneNumber: `${countryCode}${values.phoneNumber.startsWith("+") ? "" : ""}${values.phoneNumber}`,
      };

      const response = await requestFn(partnerId, finalValues);

      resetForm();
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 1000);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const formik = useFormik<QuoteFormValues>({
    initialValues,
    validationSchema: validationSchema.shape({
      services: hasPlannerCategories
        ? Yup.array().min(1, "At least one service category is required")
        : Yup.array().optional(),
    }),
    onSubmit: (values, formikHelpers) => handleSubmit(values, formikHelpers),
  });

  const allRequiredFilled =
    !!formik.values.firstName &&
    !!formik.values.lastName &&
    !!formik.values.email &&
    !!formik.values.phoneNumber &&
    formik.values.phoneNumber.length >= 10 &&
    (!hasPlannerCategories ? true : formik.values.services.length > 0);

  const isSubmitDisabled = formik.isSubmitting;

  const handleDeleteService = (service: string) => {
    const updated = formik.values.services.filter((s) => s !== service);
    formik.setFieldValue("services", updated);
  };

  const renderLabel = (text: string, required?: boolean) => (
    <Box className="quote-form__field-label">
      {text}
      {required && <span className="quote-form__required">*</span>}
    </Box>
  );

  return (
    <Box className="quote-form">
      {/* Header */}
      <Box className="quote-form__header">
        <Typography className="quote-form__title">Request Quote</Typography>
        <Typography className="quote-form__description">
          Use this form to reach out and share your preneed planning preferences
        </Typography>
      </Box>
      <form onSubmit={formik.handleSubmit} noValidate>
        <Box
          className="quote-form__grid"
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(0, 0, 0, 0.23)",
            },
            "& .MuiOutlinedInput-root:not(.Mui-error):hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(0, 0, 0, 0.23)",
            },
            "& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline":
            {
              borderWidth: "1px",
              borderColor: "rgba(0, 0, 0, 0.23)",
            },
            "& .MuiFormHelperText-root": {
              margin: 0,
              padding: 0,
              lineHeight: "16px",
            },
          }}
        >
          {/* First Name */}
          <Box>
            {renderLabel("First Name", true)}
            <TextField
              name="firstName"
              placeholder="Enter First Name"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={!!(formik.touched.firstName && formik.errors.firstName)}
              helperText={formik.touched.firstName && formik.errors.firstName}
              size="small"
              fullWidth
              inputProps={{ maxLength: 50 }}
            />
          </Box>

          {/* Last Name */}
          <Box>
            {renderLabel("Last Name", true)}
            <TextField
              name="lastName"
              placeholder="Enter Last Name"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={!!(formik.touched.lastName && formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
              size="small"
              fullWidth
              inputProps={{ maxLength: 50 }}
            />
          </Box>

          {/* Email */}
          <Box>
            {renderLabel("Email", true)}
            <TextField
              name="email"
              placeholder="Enter Email Id"
              value={formik.values.email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const next = enforceEmailPartLimits(e.target.value);
                formik.setFieldValue("email", next);
              }}
              onBlur={formik.handleBlur}
              error={!!(formik.touched.email && formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              size="small"
              fullWidth
              inputProps={{ maxLength: 150, inputMode: "email" }}
            />
          </Box>

          {/* Phone */}
          <Box>
            {renderLabel("Phone Number", true)}
            <Box
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                  height: "39px",
                  width: "100% !important",
                },
                "& .MuiInputBase-root": {
                  fontSize: "14px",
                  height: "40px",
                  padding: "10px 6px",
                },
                "& input": { color: "#104145" },
              }}
            >
              <NewPhoneNumberComponent
                value={formik.values.phoneNumber}
                onChange={(value) => formik.setFieldValue("phoneNumber", value)}
                countryCode={countryCode}
                onCountryChange={setCountryCode}
                placeholder="Enter Phone Number"
                onBlur={formik.handleBlur}
                error={
                  formik.touched.phoneNumber &&
                  Boolean(formik.errors.phoneNumber)
                }
                showHelper={true}
                helperText={(formik.touched.phoneNumber && formik.errors.phoneNumber) || ""}
              />
            </Box>
          </Box>

          {/* Services */}
          <Box>
            {renderLabel("Select Service Categories", hasPlannerCategories)}
            <FormControl size="small" fullWidth>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={uniquePlannerCategories}
                noOptionsText={!hasPlannerCategories ? "no service categories" : "No options"}
                value={formik.values.services}
                onChange={(_e, newValue) =>
                  formik.setFieldValue("services", newValue)
                }
                onBlur={formik.handleBlur}
                isOptionEqualToValue={(option, value) => option === value}
                renderTags={() => null}
                renderOption={(props, option, { selected }) => (
                  <li {...props} style={{ justifyContent: "space-between" }}>
                    {option}
                    <Checkbox
                      checked={selected}
                      sx={{
                        "&.Mui-checked": {
                          color: "#104145",
                        },
                      }}
                    />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder={hasPlannerCategories ? "Search categories to add" : "no service categories"}
                    error={!!(formik.touched.services && formik.errors.services)}
                    helperText={(formik.touched.services && typeof formik.errors.services === 'string' ? formik.errors.services : '') || ""}
                  />
                )}
                fullWidth
                slotProps={{
                  paper: {
                    sx: {
                      "& .MuiAutocomplete-option[aria-selected='true']": {
                        backgroundColor: "transparent",
                      },
                      "& .MuiAutocomplete-option.Mui-focused": {
                        backgroundColor: "transparent",
                      },
                      "& .MuiAutocomplete-option:hover": {
                        backgroundColor: "rgba(0,0,0,0.01)",
                      },
                    },
                  },
                }}
              />
            </FormControl>

            {formik.values.services.length > 0 && (
              <Box className="quote-form__chip-container">
                {formik.values.services.map((service) => (
                  <Chip
                    key={service}
                    label={service}
                    onDelete={() => handleDeleteService(service)}
                    size="small"
                    variant="outlined"
                    className="quote-form__chip"
                  />
                ))}
              </Box>
            )}
          </Box>

          {/* Message */}
          <Box>
            {renderLabel("Message")}
            <TextField
              name="message"
              placeholder="Tell us about your wishes and planning needs"
              value={formik.values.message}
              onChange={formik.handleChange}
              multiline
              rows={2}
              size="small"
              fullWidth
              inputProps={{ maxLength: 200 }}
            />
          </Box>
        </Box>

        <Box className="quote-form__actions">
          <Button
            type="submit"
            variant="outlined"
            disabled={isSubmitDisabled}
            className="quote-form__actions-btn"
          >
            Request Quote
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default QuoteFormContent;
